import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const OurTeam = () => {
  const [messages, setMessages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // Touch State
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50; // Threshold for a valid swipe

  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        const response = await axios.get(
          "https://ethiccraft.org/backend/team/read_isShown.php",
        );
        const data = response.data;

        const filteredData = data
          .filter((item) => item.college === "main")
          .map((item) => ({
            image: `https://ethiccraft.org/${item.image}`,
            name: item.name,
            message: item.description,
            author: item.designation,
          }));

        setMessages(filteredData);
      } catch (err) {
        console.error("Error fetching team data:", err);
        setError("Failed to load team data.");
      }
    };

    fetchTeamData();
  }, []);

  // --- NAVIGATION LOGIC ---
  const handleSlide = useCallback(
    (direction) => {
      if (isAnimating || messages.length === 0) return;
      setIsAnimating(true);

      setTimeout(() => {
        if (direction === "next") {
          setCurrentIndex((prev) =>
            prev === messages.length - 1 ? 0 : prev + 1,
          );
        } else {
          setCurrentIndex((prev) =>
            prev === 0 ? messages.length - 1 : prev - 1,
          );
        }
        setIsAnimating(false);
      }, 500);
    },
    [isAnimating, messages.length],
  );

  // --- KEYBOARD SUPPORT ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        handleSlide("prev");
      } else if (e.key === "ArrowRight") {
        handleSlide("next");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSlide]);

  // --- TOUCH SUPPORT ---
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleSlide("next");
    }
    if (isRightSwipe) {
      handleSlide("prev");
    }
  };

  if (error) return <div className="error-message">{error}</div>;
  if (messages.length === 0)
    return <div className="loading-message">Loading team members...</div>;

  const currentProfile = messages[currentIndex];

  return (
    <section
      className="premium-team-section"
      // Attach touch handlers to the main container
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="premium-bg-glow"></div>
      <div className="noise-overlay"></div>

      <div className="premium-card-wrapper">
        {/* Left Side: Image */}
        <div className="premium-image-box">
          <div className={`img-frame ${isAnimating ? "animating" : ""}`}>
            <img src={currentProfile.image} alt={currentProfile.name} />
          </div>

          {/* Navigation Buttons Over Image */}
          <div className="nav-controls">
            <button
              onClick={() => handleSlide("prev")}
              className="nav-btn"
              aria-label="Previous member"
            >
              ←
            </button>
            <div className="slide-counter">
              {currentIndex + 1} <span className="divider">/</span>{" "}
              {messages.length}
            </div>
            <button
              onClick={() => handleSlide("next")}
              className="nav-btn"
              aria-label="Next member"
            >
              →
            </button>
          </div>
        </div>

        {/* Right Side: Content */}
        <div className="premium-content-box">
          <div className="watermark-quote">“</div>

          <div
            className={`text-content-inner ${isAnimating ? "fade-out" : "fade-in"}`}
          >
            <h2 className="premium-header">Our Leadership</h2>

            <p className="premium-message">{currentProfile.message}</p>

            <div className="premium-divider"></div>

            <div className="premium-identity">
              <h3 className="premium-name">{currentProfile.name}</h3>
              <p className="premium-designation">{currentProfile.author}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurTeam;
